import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { dispatch } from '../../redux/store';
import { PlayerPosition, PreferredFoot } from '../../utils/constants';
import { createProfile1 } from '../../utils/formSchema';
import { cleanDate, playerDateThreshold } from '../../utils/helpers';
import { BigButtons } from '../reusable/buttons/Buttons';
import LocationSelect from '../reusable/LocationSelect';
import DropdownWithCheckBox from './Custom/DropdownWithCheckBox';

const CreateProfileForm = ({ s3Loading }) => {
    const navigate = useNavigate();

    const { userInfo } = useSelector(state => state.auth.authUser);

    const updateUserLoading = useSelector(
        ({ loading }) => loading.effects.user.updateUser
    );

    const onSubmit = async values => {
        const mergedValues = {
            ...values,
            id: userInfo.id
        };

        for (const key in mergedValues) {
            if (!mergedValues[key]) delete mergedValues[key];
        }
        const res = await dispatch.user.updateUser(mergedValues);

        if (res === 1) {
            navigate(
                mergedValues.userType !== 'NON_PLAYER'
                    ? '/contract-select'
                    : '/work-status'
            );
        }
    };

    const initialValues = {
        position: userInfo.position || '',
        secondaryPositions: userInfo.secondaryPositions || [],
        birthday: userInfo.birthday || '',
        preferredFoot: userInfo.preferredFoot || '',
        location: userInfo.location || '',
        locationId: userInfo.locationId || '',
        district: userInfo.district || '',
        locality: userInfo.locality || '',
        region: userInfo.region || '',
        country: userInfo.country || '',
        countryCode: userInfo.countryCode || '',
        height: userInfo.height || '',
        gender: userInfo.gender || '',
        userType: userInfo?.userType || 'PLAYER',
        nonPlayerRole: userInfo?.nonPlayerRole || []
    };

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue
    } = useFormik({
        initialValues,
        onSubmit,
        validationSchema: createProfile1
    });

    const handleRoleChange = (roles, fieldName = 'nonPlayerRole') => {
        setFieldValue(fieldName, roles);
    };

    return (
        <div className='mt-7'>
            <form onSubmit={handleSubmit} className='w-full'>
                {userInfo.userType !== 'NON_PLAYER' && (
                    <>
                        <div className='mt-7'>
                            <label className='font-favela-bold uppercase'>
                                Primary Position
                            </label>
                            <div
                                className={`flex items-center border-b ${errors.position && touched.position ? 'border-red-600' : 'border-black'} py-1`}
                            >
                                <div className='appearance-none bg-transparent border-none w-full text-[#000000] mr-3 py-1 leading-tight focus:outline-none font-[400]'>
                                    <DropdownWithCheckBox
                                        label='Select position'
                                        allowMultiple={false}
                                        allowSearch={false}
                                        defaultOptions={
                                            values.position
                                                ? [values.position]
                                                : []
                                        }
                                        listOfOptions={
                                            Object.entries(PlayerPosition).map(
                                                ([value, label]) => ({
                                                    value,
                                                    label
                                                })
                                            ) || []
                                        }
                                        valueKey='value'
                                        labelKey='label'
                                        onChange={options => {
                                            const selectedValue =
                                                options && options.length > 0
                                                    ? options[0]
                                                    : '';
                                            setFieldValue(
                                                'position',
                                                selectedValue
                                            );

                                            // Remove the selected primary position from secondary positions if it exists
                                            if (
                                                selectedValue &&
                                                values.secondaryPositions
                                            ) {
                                                const updatedSecondary =
                                                    values.secondaryPositions.filter(
                                                        pos =>
                                                            pos !==
                                                            selectedValue
                                                    );
                                                if (
                                                    updatedSecondary.length !==
                                                    values.secondaryPositions
                                                        .length
                                                ) {
                                                    setFieldValue(
                                                        'secondaryPositions',
                                                        updatedSecondary
                                                    );
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <p className='absolute flex justify-center text-red-600 text-xs'>
                                {errors.position && touched.position
                                    ? errors.position
                                    : ''}
                            </p>
                        </div>

                        <div className='mt-7'>
                            <label className='font-favela-bold uppercase'>
                                Secondary Position(s)
                            </label>
                            <div
                                className={`flex items-center border-b ${errors.secondaryPositions && touched.secondaryPositions ? 'border-red-600' : 'border-black'} py-1`}
                            >
                                <div className='appearance-none bg-transparent border-none w-full text-[#000000] mr-3 py-1 leading-tight focus:outline-none font-[400]'>
                                    <DropdownWithCheckBox
                                        label='Select position(s)'
                                        allowMultiple={true}
                                        maxSelection={3}
                                        allowSearch={false}
                                        defaultOptions={
                                            values.secondaryPositions || []
                                        }
                                        listOfOptions={
                                            Object.entries(PlayerPosition).map(
                                                ([value, label]) => ({
                                                    value,
                                                    label,
                                                    disabled:
                                                        value ===
                                                        values.position
                                                })
                                            ) || []
                                        }
                                        valueKey='value'
                                        labelKey='label'
                                        onChange={options =>
                                            handleRoleChange(
                                                options,
                                                'secondaryPositions'
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <p className='absolute flex justify-center text-red-600 text-xs'>
                                {errors.secondaryPositions &&
                                touched.secondaryPositions
                                    ? errors.secondaryPositions
                                    : ''}
                            </p>
                        </div>
                    </>
                )}

                {userInfo.userType === 'NON_PLAYER' && (
                    <div className='mt-7'>
                        <label className='font-favela-bold uppercase'>
                            Role
                        </label>
                        <div
                            className={`flex items-center border-b ${errors.nonPlayerRole && touched.nonPlayerRole ? 'border-red-600' : 'border-black'} py-1`}
                        >
                            <div className='appearance-none bg-transparent border-none w-full text-[#000000] mr-3 py-1 leading-tight focus:outline-none font-[400]'>
                                <DropdownWithCheckBox
                                    defaultOptions={values.nonPlayerRole}
                                    onChange={handleRoleChange}
                                    maxSelection={3}
                                />
                            </div>
                        </div>

                        <p className='absolute flex justify-center text-red-600 text-xs'>
                            {errors.nonPlayerRole && touched.nonPlayerRole
                                ? errors.nonPlayerRole
                                : ''}
                        </p>
                    </div>
                )}

                {userInfo.userType !== 'NON_PLAYER' && (
                    <div className='mt-7'>
                        <label className='font-favela-bold uppercase'>
                            Preferred Foot
                        </label>
                        <div
                            className={`flex items-center border-b ${errors.preferredFoot && touched.preferredFoot ? 'border-red-600' : 'border-black'} py-1`}
                        >
                            <div className='appearance-none bg-transparent border-none w-full text-[#000000] mr-3 py-1 leading-tight focus:outline-none font-[400]'>
                                <DropdownWithCheckBox
                                    label='Select Foot'
                                    allowMultiple={false}
                                    allowSearch={false}
                                    defaultOptions={
                                        values.preferredFoot
                                            ? [values.preferredFoot]
                                            : []
                                    }
                                    listOfOptions={
                                        Object.entries(PreferredFoot).map(
                                            ([value, label]) => ({
                                                value,
                                                label
                                            })
                                        ) || []
                                    }
                                    valueKey='value'
                                    labelKey='label'
                                    onChange={options => {
                                        const selectedValue =
                                            options && options.length > 0
                                                ? options[0]
                                                : '';
                                        setFieldValue(
                                            'preferredFoot',
                                            selectedValue
                                        );
                                    }}
                                />
                            </div>
                        </div>
                        <p className='absolute flex justify-center text-red-600 text-xs'>
                            {errors.preferredFoot && touched.preferredFoot
                                ? errors.preferredFoot
                                : ''}
                        </p>
                    </div>
                )}

                {userInfo.userType !== 'NON_PLAYER' && (
                    <div className='mt-7'>
                        <label
                            htmlFor='birthday'
                            className='font-favela-bold uppercase'
                        >
                            Date of Birth
                        </label>
                        <div
                            className={`flex items-center border-b ${errors.birthday && touched.birthday ? 'border-red-600' : 'border-black'} py-2`}
                        >
                            <div
                                className={`appearance-none bg-transparent border-none w-full ${errors.birthday && touched.birthday ? 'text-red-600' : 'text-[#00000]'} text-[14px] mr-3 py-3 leading-[24px] focus:outline-none relative`}
                            >
                                {!values.birthday && (
                                    <div className='absolute inset-0 flex items-center pointer-events-none text-gray-400 text-[14px]'>
                                        DD/MM/YYYY
                                    </div>
                                )}
                                <input
                                    name='birthday'
                                    id='birthday'
                                    type='date'
                                    className={`bg-[#ffffff] text-left ${errors.birthday && touched.birthday ? 'calendar-red' : ''} ${!values.birthday ? 'text-transparent' : ''}`}
                                    onChange={e => {
                                        setFieldValue(
                                            'birthday',
                                            e.target.value
                                                ? cleanDate(e.target.value)
                                                : ''
                                        );
                                    }}
                                    onFocus={e => {
                                        e.target.classList.remove(
                                            'text-transparent'
                                        );
                                        e.target.classList.add('text-black');
                                    }}
                                    onBlur={e => {
                                        if (!e.target.value) {
                                            e.target.classList.add(
                                                'text-transparent'
                                            );
                                            e.target.classList.remove(
                                                'text-black'
                                            );
                                        }
                                    }}
                                    max={playerDateThreshold}
                                    value={
                                        values.birthday
                                            ? cleanDate(values.birthday)
                                            : ''
                                    }
                                    onInvalid={e => {
                                        e.target.setCustomValidity(
                                            'You must be at least 16 years old to create an account.'
                                        );
                                    }}
                                    onInput={e => {
                                        e.target.setCustomValidity('');
                                    }}
                                />
                            </div>
                        </div>
                        <p className='absolute flex justify-center text-red-600 text-xs'>
                            {errors.birthday && touched.birthday
                                ? errors.birthday
                                : ''}
                        </p>
                    </div>
                )}
                <div className='mt-7'>
                    <label className='font-favela-bold uppercase'>
                        Location
                    </label>
                    <LocationSelect
                        selectedLocation={{
                            id: values.locationId,
                            name: values.location
                        }}
                        setSelectedLocation={selectedLocation => {
                            setFieldValue('location', selectedLocation.name);
                            setFieldValue('locationId', selectedLocation.id);
                            setFieldValue(
                                'district',
                                selectedLocation.district
                            );
                            setFieldValue(
                                'locality',
                                selectedLocation.locality
                            );
                            setFieldValue('region', selectedLocation.region);
                            setFieldValue('country', selectedLocation.country);
                            setFieldValue(
                                'countryCode',
                                selectedLocation.countryCode
                            );
                        }}
                        borderColor={errors.locationId ? 'border-red-600' : ''}
                        className='absolute z-10'
                        onBlur={handleBlur}
                        name='locationId'
                    />
                    <p className='absolute flex justify-center text-red-600 text-xs'>
                        {errors.locationId ? errors.locationId : ''}
                    </p>
                </div>

                {userInfo.userType !== 'NON_PLAYER' && (
                    <div className='mt-7'>
                        <label
                            htmlFor='height'
                            className='font-favela-bold uppercase'
                        >
                            Height (m)
                        </label>
                        <div
                            className={`flex items-center border-b ${errors.height && touched.height ? 'border-red-600' : 'border-black'} py-2`}
                        >
                            <input
                                className='appearance-none bg-transparent border-none w-full text-gray-700 mr-3 py-1 leading-tight focus:outline-none'
                                type='text'
                                placeholder='e.g. 1.83'
                                value={values.height}
                                inputMode={'decimal'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name='height'
                                id='height'
                            />
                        </div>
                        <p className='absolute flex justify-center text-red-600 text-xs'>
                            {errors.height && touched.height
                                ? errors.height
                                : ''}
                        </p>
                    </div>
                )}
                {/* Used to help validate non players. Not visibale for users */}
                <input name='userType' hidden value={userInfo.userType} />

                {userInfo.userType === 'PLAYER' && (
                    <div className='mt-7'>
                        <label
                            htmlFor='gender'
                            className='font-favela-bold uppercase'
                        >
                            I PLAY…
                        </label>
                        <div
                            className={`flex items-center border-b ${errors.gender && touched.gender ? 'border-red-600' : 'border-black'}`}
                        >
                            <select
                                value={values.gender}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name='gender'
                                id='gender'
                                className='appearance-none bg-transparent border-none w-full text-[#000000] text-[14px] mr-3 py-2 leading-[24px] focus:outline-none'
                            >
                                <option disabled={true} value=''>
                                    - -
                                </option>
                                <option value='MEN'>Men’s football</option>
                                <option value='WOMEN'>Women’s football</option>
                                {userInfo.userType === 'NON_PLAYER' && (
                                    <option value='BOTH'>
                                        Both, men’s and women’s football
                                    </option>
                                )}
                            </select>
                        </div>
                        <p className='absolute flex justify-center text-red-600 text-xs'>
                            {errors.gender && touched.gender
                                ? errors.gender
                                : ''}
                        </p>
                    </div>
                )}
                <div
                    className={`left-0 right-0 p-4 mt-10 ${userInfo.userType === 'NON_PLAYER' ? 'fixed bottom-0' : ''}`}
                >
                    <BigButtons
                        isLoading={!s3Loading && updateUserLoading}
                        black
                        label={
                            userInfo.userType === 'NON_PLAYER' ? 'Next' : 'Done'
                        }
                    />
                </div>
            </form>
        </div>
    );
};

export default CreateProfileForm;
